import { AnimationDataTexture } from '../texture';
import { Context } from '../context/Context';
import { Scene } from '../scene/Scene';
import { RenderObject } from '../object/RenderObject';
import { Animation } from '../animation';
import { BoundingDimensions } from '../util';
import { VertexBuffer } from './VertexBuffer';

export abstract class Mesh {
  public buffer!: VertexBuffer;
  public indexBuffer?: VertexBuffer;
  public dataLayout!: MeshLayout;
  public animation?: Animation;
  public animationDataTexture?: AnimationDataTexture;
  public vao!: WebGLVertexArrayObject;
  private destroyed = false;

  /**
   * Local-space bounds, populated by subclasses during geometry construction.
   * `RenderGroup` builders read this to compute per-instance world-space
   * bounding spheres without going through the per-RenderObject
   * `BoundingDimensions.bind(trs)` path. Subclasses should assign before
   * any RenderGroup-style instantiation runs.
   */
  protected _localBounds?: BoundingDimensions;

  public get localBounds(): BoundingDimensions {
    if (!this._localBounds) {
      throw new Error(`Mesh '${this.reference}' has no localBounds set yet`);
    }
    return this._localBounds;
  }

  public constructor(
    public reference: string,
    public ctx: Context,
    public scene: Scene,
  ) {}

  public abstract createInstance(): RenderObject;

  public getAttributeBinding(segment: MeshLayoutSegment): AttributeBindSettings {
    return {
      length: segment.length,
      offset: segment.offset,
      itemSize: this.buffer.itemSize,
    };
  }

  public createVAO(gl: WebGL2RenderingContext): void {
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);

    const stride = this.buffer.itemSize * Float32Array.BYTES_PER_ELEMENT;

    if (this.dataLayout.position) this.bindVAOAttribute(gl, 0, this.dataLayout.position, stride);
    if (this.dataLayout.normal) this.bindVAOAttribute(gl, 1, this.dataLayout.normal, stride);
    if (this.dataLayout.diffuseTexture)
      this.bindVAOAttribute(gl, 2, this.dataLayout.diffuseTexture, stride);
    if (this.dataLayout.color) this.bindVAOAttribute(gl, 3, this.dataLayout.color, stride);
    if (this.dataLayout.jointWeights)
      this.bindVAOAttribute(gl, 4, this.dataLayout.jointWeights, stride);
    if (this.dataLayout.jointIndices)
      this.bindVAOAttribute(gl, 5, this.dataLayout.jointIndices, stride);

    if (this.indexBuffer) {
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer.glVertexBuffer);
    }

    gl.bindVertexArray(null);
  }

  public destroy(gl: WebGL2RenderingContext): void {
    // Idempotent — meshes are commonly shared across multiple RenderObjects
    // (instancing, model registry), so destroy() may be called more than once.
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.vao) gl.deleteVertexArray(this.vao);
    if (this.buffer?.glVertexBuffer) gl.deleteBuffer(this.buffer.glVertexBuffer);
    if (this.indexBuffer?.glVertexBuffer) gl.deleteBuffer(this.indexBuffer.glVertexBuffer);
    if (this.animationDataTexture?.glTexture) gl.deleteTexture(this.animationDataTexture.glTexture);
  }

  private bindVAOAttribute(
    gl: WebGL2RenderingContext,
    location: number,
    segment: MeshLayoutSegment,
    stride: number,
  ): void {
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(
      location,
      segment.length,
      gl.FLOAT,
      false,
      stride,
      segment.offset * Float32Array.BYTES_PER_ELEMENT,
    );
  }
}

export type AttributeBindSettings = {
  length: number;
  offset: number;
  itemSize: number;
};

export type MeshLayout = {
  position?: MeshLayoutSegment;
  normal?: MeshLayoutSegment;
  color?: MeshLayoutSegment;
  diffuseTexture?: MeshLayoutSegment;
  jointWeights?: MeshLayoutSegment;
  jointIndices?: MeshLayoutSegment;
  lightMap?: MeshLayoutSegment;
  bakedLight?: MeshLayoutSegment;
};

export type MeshLayoutSegment = {
  offset: number;
  length: number;
};
